import { parseNumericInput as parseFormattedNumericInput } from '../../utils/numberInput';

function parseNumericInput(value) {
  if (!/^-?[\d\s'.,]+$/.test(String(value))) return NaN;
  return parseFormattedNumericInput(value);
}

export function validateForm(formData) {
  const errors = [];
  const warnings = [];

  const {
    activitySpenglerarbeiten,
    activityLackierarbeiten,
    activityWeitere,
    productiveFte,
    totalAreaM2,
    productiveAreaM2,
    electricityMix,
    electricityValue,
    electricityUnit,
    heatingSystems,
    paintLiters,
    paintBooths,
    airDryingPaintShare,
    repairsPerYear,
    repairTypeShares,
    hasPublicChargingStations,
    publicChargingPointsCount,
    pvStatus,
    pvProductionKwh,
    pvFeedInKwh,
    repairTypes,
    materials,
    garageType,
    garageBrands,
    buildingRelationship,
    soldHoursTotal,
    splitSoldHours,
    soldHoursPaint,
    soldHoursBody,
    gasShutdownPlanned,
    gasShutdownYear,
    oilHeatingReplacementPlanned,
    oilHeatingReplacementYear,
    fleetImportSuccessful,
    fleetImportedVehicles,
    combustionCount,
    combustionAvgAge,
    combustionAvgKm,
    electricCount,
    electricAvgAge,
    electricAvgKm,
    hybridCount,
    hybridAvgAge,
    hybridAvgKm,
  } = formData;

  function hasValue(value) {
    return (
      value !== '' &&
      value !== null &&
      value !== undefined
    );
  }

  function hasValidNumber(value) {
    return hasValue(value) && Number.isFinite(parseNumericInput(value));
  }

  function checkNumber(step, field, value, label, { optional = false, positive = false, integer = false } = {}) {
    if (optional && !hasValue(value)) return;
    const number = parseNumericInput(value);
    if (!hasValidNumber(value) || (positive ? number <= 0 : number < 0) || (integer && !Number.isInteger(number))) {
      errors.push({ step, field, message: `${label}: Bitte ${integer ? 'eine ganze Zahl' : 'einen Zahlenwert'} ${positive ? 'grösser als 0' : 'grösser oder gleich 0'} eingeben.` });
    }
  }

  function checkBuildYear(step, field, value, label) {
    if (!hasValue(value)) return;
    if (!/^[1-9]\d{3}$/.test(String(value)) || Number(value) > new Date().getFullYear()) {
      errors.push({ step, field, message: `${label}: Das Baujahr muss vierstellig sein und darf nicht in der Zukunft liegen.` });
    }
  }

  // Lackierkabinen
  const booths = Array.isArray(paintBooths) ? paintBooths : [];

  if (booths.length === 0) {
    errors.push({ step: 3, field: 'paintBooths', message: 'Mindestens eine Lackierkabine ist erforderlich.' });
  }

  booths.forEach((booth, index) => {
    const prefix = `paintBooths.${index}`;
    const label = `Lackierkabine ${index + 1}`;
    checkBuildYear(3, `${prefix}.buildYear`, booth.buildYear, label);
    if (!booth.type) errors.push({ step: 3, field: `${prefix}.type`, message: `${label}: Bitte einen Typ auswählen.` });
    if (!booth.dryingType) errors.push({ step: 3, field: `${prefix}.dryingType`, message: `${label}: Bitte eine Trocknungsart angeben.` });
    checkNumber(3, `${prefix}.dryingTemp`, booth.dryingTemp, `${label}, Trocknungstemperatur`, { optional: true });
    checkNumber(3, `${prefix}.dryingTime`, booth.dryingTime, `${label}, Trocknungszeit`, { optional: true, positive: true });
    const temperature = parseNumericInput(booth.dryingTemp);
    if (Number.isFinite(temperature) && temperature >= 0 && (temperature < 25 || temperature > 90)) {
      warnings.push({ step: 3, field: `${prefix}.dryingTemp`, message: `${label}: Bitte die Trocknungstemperatur von ${temperature} °C prüfen (ausserhalb 25–90 °C).` });
    }
    const requiresEnergy =
      booth.separatelyHeated === 'Ja';

    if (!requiresEnergy) return;

    if (!booth.burnerEnergy) {
      errors.push({
        step: 3,
        field: `paintBooths.${index}.burnerEnergy`,
        message: `Brenner-Energie für Lackierkabine ${index + 1} ist erforderlich.`,
      });
    }

    if (!['kwh', 'liter', 'tonnen', 'chf'].includes(booth.energyConsumptionUnit)) {
      errors.push({
        step: 3,
        field: `paintBooths.${index}.energyConsumptionUnit`,
        message: `Einheit für Lackierkabine ${index + 1} ist erforderlich.`,
      });
    }

    if (!hasValidNumber(booth.energyConsumption)) {
      errors.push({
        step: 3,
        field: `paintBooths.${index}.energyConsumption`,
        message: `Energieverbrauch für Lackierkabine ${index + 1} ist erforderlich.`,
      });
    }
  });

  // Betrieb & Aktivitäten – neu Schritt 1
  const activitySum =
    (parseFloat(activitySpenglerarbeiten) || 0) +
    (parseFloat(activityLackierarbeiten) || 0) +
    (parseFloat(activityWeitere) || 0);

  if (activitySum !== 100) {
    const diff = activitySum - 100;

    errors.push({
      step: 1,
      field: 'activity',
      message:
        diff > 0
          ? `Tätigkeitssumme ist ${diff} % zu viel (aktuell ${activitySum} %).`
          : `Tätigkeitssumme fehlt ${Math.abs(diff)} % (aktuell ${activitySum} %).`,
    });
  }

  if (!garageType) {
    errors.push({
      step: 1,
      field: 'garageType',
      message: 'Garagentyp ist erforderlich.',
    });
  }

  if (
    garageType === 'brand_bound' &&
    !String(garageBrands || '').trim()
  ) {
    errors.push({
      step: 1,
      field: 'garageBrands',
      message:
        'Bei einer markengebundenen Garage muss mindestens eine Marke angegeben werden.',
    });
  }

  if (!buildingRelationship) {
    errors.push({
      step: 1,
      field: 'buildingRelationship',
      message: 'Gebäudeverhältnis ist erforderlich.',
    });
  }

  if (!hasValidNumber(productiveFte) || parseNumericInput(productiveFte) <= 0) {
    errors.push({
      step: 1,
      field: 'productiveFte',
      message: 'Anzahl produktive Mitarbeitende (FTE) ist erforderlich und muss grösser als 0 sein.',
    });
  }

  const soldHours = parseNumericInput(soldHoursTotal);

  if (
    soldHoursTotal === '' ||
    soldHoursTotal === null ||
    soldHoursTotal === undefined ||
    !Number.isFinite(soldHours) || soldHours < 0
  ) {
    errors.push({
      step: 1,
      field: 'soldHoursTotal',
      message:
        'Anzahl verkaufte Stunden ist erforderlich und muss grösser oder gleich 0 sein.',
    });
  }

  if (splitSoldHours) {
    const paintHours = parseNumericInput(soldHoursPaint);
    const bodyHours = parseNumericInput(soldHoursBody);

    if (
      !Number.isFinite(paintHours) ||
      paintHours < 0 ||
      !Number.isFinite(bodyHours) ||
      bodyHours < 0
    ) {
      errors.push({
        step: 1,
        field: 'soldHoursSplit',
        message:
          'Für Lackiererei und Spenglerei müssen gültige Stundenwerte erfasst werden.',
      });
    } else if (
      !Number.isNaN(soldHours) &&
      Math.abs(paintHours + bodyHours - soldHours) > 0.000001
    ) {
      errors.push({
        step: 1,
        field: 'soldHoursSplit',
        message:
          `Die Aufteilung ergibt ${paintHours + bodyHours} Stunden, ` +
          `muss aber dem Gesamtwert von ${soldHours} Stunden entsprechen.`,
      });
    }
  }

  checkNumber(1, 'annualRevenue', formData.annualRevenue, 'Jahresumsatz', { optional: true });
  if (formData.splitRevenue) {
    const parts = ['revenuePaint', 'revenueBody'];
    parts.forEach((field, index) => checkNumber(1, field, formData[field], `Teilumsatz ${index === 0 ? 'Lackiererei' : 'Spenglerei'}`, { optional: true }));
    if (parts.some(field => hasValue(formData[field]))) {
      if (!hasValue(formData.annualRevenue)) {
        errors.push({ step: 1, field: 'annualRevenue', message: 'Bei einem Teilumsatz ist der Jahresumsatz erforderlich.' });
      } else {
        const total = parseNumericInput(formData.annualRevenue);
        const sum = parts.reduce((value, field) => value + (hasValue(formData[field]) ? parseNumericInput(formData[field]) : 0), 0);
        if (Number.isFinite(total) && Number.isFinite(sum) && Math.abs(sum - total) > 0.000001) {
          parts.forEach(field => errors.push({ step: 1, field, message: 'Die Teilumsätze müssen zusammen genau den Jahresumsatz ergeben.' }));
        }
      }
    }
  }
  checkNumber(1, 'averageHourlyRate', formData.averageHourlyRate, 'Durchschnittlicher Stundensatz', { optional: true });
  if (formData.splitHourlyRate) {
    checkNumber(1, 'hourlyRatePaint', formData.hourlyRatePaint, 'Stundensatz Lackiererei', { optional: true });
    checkNumber(1, 'hourlyRateBody', formData.hourlyRateBody, 'Stundensatz Spenglerei', { optional: true });
  }

  if (!hasValidNumber(totalAreaM2) || parseNumericInput(totalAreaM2) <= 0) {
    errors.push({
      step: 1,
      field: 'totalAreaM2',
      message: 'Betriebsfläche gesamt ist erforderlich und muss grösser als 0 sein.',
    });
  }

  if (
    hasValue(productiveAreaM2) &&
    !hasValidNumber(productiveAreaM2)
  ) {
    errors.push({
      step: 1,
      field: 'productiveAreaM2',
      message:
        'Die produktive Betriebsfläche muss ein gültiger Zahlenwert sein.',
    });
  } else if (
    hasValidNumber(productiveAreaM2) &&
    parseNumericInput(productiveAreaM2) <= 0
  ) {
    errors.push({
      step: 1,
      field: 'productiveAreaM2',
      message:
        'Die produktive Betriebsfläche muss grösser als 0 sein.',
    });
  } else if (
    hasValidNumber(productiveAreaM2) &&
    hasValidNumber(totalAreaM2) &&
    parseNumericInput(productiveAreaM2) > parseNumericInput(totalAreaM2)
  ) {
    errors.push({
      step: 1,
      field: 'productiveAreaM2',
      message:
        'Die produktive Betriebsfläche darf nicht grösser als die gesamte Betriebsfläche sein.',
    });
  }

  // Energie & Heizung
  if (!electricityMix) {
    errors.push({
      step: 2,
      field: 'electricityMix',
      message: 'Strommix ist erforderlich.',
    });
  }

  if (
    electricityUnit !== 'unknown' &&
    (!hasValidNumber(electricityValue) || parseNumericInput(electricityValue) <= 0)
  ) {
    errors.push({
      step: 2,
      field: 'electricityValue',
      message: 'Jährlicher Strombezug muss grösser als 0 sein.',
    });
  }

  const systems =
    Array.isArray(heatingSystems) && heatingSystems.length > 0
      ? heatingSystems
      : [];

  if (systems.length === 0) {
    errors.push({
      step: 2,
      field: 'heatingSystems',
      message: 'Mindestens eine Heizart ist erforderlich.',
    });
  }

  systems.forEach((system, index) => {
    if (!system.type) {
      errors.push({
        step: 2,
        field: `heatingSystems.${index}.type`,
        message: `Heizart ${index + 1} ist erforderlich.`,
      });
    }

    if (!system.unit || (parseNumericInput(system.value) > 0 && system.unit === 'unknown')) {
      errors.push({
        step: 2,
        field: `heatingSystems.${index}.unit`,
        message: `Einheit für Heizart ${index + 1} ist erforderlich.`,
      });
    }

    if (
      system.unit !== 'unknown' &&
      (!hasValidNumber(system.value) || parseNumericInput(system.value) <= 0)
    ) {
      errors.push({
        step: 2,
        field: `heatingSystems.${index}.value`,
        message:
          `Jährlicher Heizverbrauch für Heizart ${index + 1} muss grösser als 0 sein.`,
      });
    }
  });


  if (Number(formData.reportingYear) < 2020) {
    errors.push({ step: 0, field: 'reportingYear', message: 'Das Erhebungsjahr darf nicht vor 2020 liegen.' });
  }

  [
    { planned: gasShutdownPlanned, value: gasShutdownYear, field: 'gasShutdownYear', type: 'Erdgas', label: 'Gasnetzabschaltung' },
    { planned: oilHeatingReplacementPlanned, value: oilHeatingReplacementYear, field: 'oilHeatingReplacementYear', type: 'Heizöl', label: 'Ölheizungsersatz' },
  ].forEach(plan => {
    if (!plan.planned) return;
    if (!/^[1-9]\d{3}$/.test(String(plan.value)) || Number(plan.value) < Number(formData.reportingYear)) {
      errors.push({ step: 2, field: plan.field, message: `${plan.label}: Bitte ein vierstelliges Jahr ab dem Erhebungsjahr ${formData.reportingYear} angeben.` });
    }
    if (!systems.some(system => system.type === plan.type)) {
      warnings.push({ step: 2, field: plan.field, message: `${plan.label} geplant, aber keine Heizart ${plan.type} erfasst. Bitte prüfen.` });
    }
  });

  if (!['vorhanden', 'keine', 'planung'].includes(pvStatus)) {
    errors.push({ step: 2, field: 'pvStatus', message: 'PV-Status ist erforderlich.' });
  }


  // Lackieren & Reparieren
  if (!hasValidNumber(paintLiters) || parseNumericInput(paintLiters) < 0) {
    errors.push({
      step: 3,
      field: 'paintLiters',
      message: 'Eingekaufter Lack (Liter/Jahr) muss numerisch und grösser oder gleich 0 sein.',
    });
  }

  if (
    airDryingPaintShare === '' ||
    airDryingPaintShare === null ||
    airDryingPaintShare === undefined
  ) {
    errors.push({
      step: 3,
      field: 'airDryingPaintShare',
      message:
        'Anteil lufttrocknender Lacksysteme ist erforderlich.',
    });
  } else {
    const share = parseNumericInput(airDryingPaintShare);

    if (
      Number.isNaN(share) ||
      share < 0 ||
      share > 100
    ) {
      errors.push({
        step: 3,
        field: 'airDryingPaintShare',
        message:
          'Anteil lufttrocknender Lacksysteme muss zwischen 0 und 100 % liegen.',
      });
    }
  }

  const yearlyRepairs = parseNumericInput(repairsPerYear);

  if (
    repairsPerYear === '' ||
    repairsPerYear === null ||
    repairsPerYear === undefined ||
    !Number.isInteger(yearlyRepairs) || yearlyRepairs < 0
  ) {
    errors.push({
      step: 3,
      field: 'repairsPerYear',
      message:
        'Reparaturen pro Jahr müssen eine ganze Zahl grösser oder gleich 0 sein.',
    });
  }

  const materialTotal = Object.values(materials || {}).reduce(
    (sum, value) => sum + (parseFloat(value) || 0),
    0
  );

  if (Math.abs(materialTotal - 100) > 0.01) {
    const diff = materialTotal - 100;

    errors.push({
      step: 3,
      field: 'materials',
      message:
        diff > 0
          ? `Die Summe der Verbrauchsmaterialien ist ${Math.abs(diff).toFixed(1)} % zu hoch (aktuell ${materialTotal.toFixed(1)} %).`
          : `Die Summe der Verbrauchsmaterialien fehlt ${Math.abs(diff).toFixed(1)} % (aktuell ${materialTotal.toFixed(1)} %).`,
    });
  }

  const selectedRepairTypes = Array.isArray(repairTypes)
    ? repairTypes
    : [];

  if (selectedRepairTypes.length === 0) {
    errors.push({
      step: 3,
      field: 'repairTypes',
      message: 'Mindestens eine Reparaturart ist erforderlich.',
    });
  }

  selectedRepairTypes.forEach(type => {
    const value = repairTypeShares?.[type];
    const share = parseNumericInput(value);

    if (
      value === '' ||
      value === null ||
      value === undefined ||
      Number.isNaN(share) ||
      share < 0 ||
      share > 100
    ) {
      errors.push({
        step: 3,
        field: `repairTypeShares.${type}`,
        message:
          `Die Reparaturquote für ${type} muss zwischen 0 und 100 % liegen.`,
      });
    }
  });

  // Weiteres
  const hasFleetImport =
    fleetImportSuccessful === true &&
    Array.isArray(fleetImportedVehicles) &&
    fleetImportedVehicles.length > 0;

  if (!hasFleetImport) {
    [
      {
        title: 'Verbrenner',
        countField: 'combustionCount',
        count: combustionCount,
        ageField: 'combustionAvgAge',
        age: combustionAvgAge,
        kmField: 'combustionAvgKm',
        km: combustionAvgKm,
      },
      {
        title: 'Elektro',
        countField: 'electricCount',
        count: electricCount,
        ageField: 'electricAvgAge',
        age: electricAvgAge,
        kmField: 'electricAvgKm',
        km: electricAvgKm,
      },
      {
        title: 'Hybrid',
        countField: 'hybridCount',
        count: hybridCount,
        ageField: 'hybridAvgAge',
        age: hybridAvgAge,
        kmField: 'hybridAvgKm',
        km: hybridAvgKm,
      },
    ].forEach(row => {
      checkNumber(4, row.countField, row.count, `Anzahl Ersatzwagen ${row.title}`, { optional: true, integer: true });
      if (hasValidNumber(row.count) && parseNumericInput(row.count) > 0) {
        checkNumber(4, row.ageField, row.age, `Durchschnittsalter ${row.title}`, { optional: true });
        checkNumber(4, row.kmField, row.km, `Jahreskilometer ${row.title}`, { optional: true, positive: true });
      }
    });
  }

  checkNumber(3, 'sparePartsValueCHF', formData.sparePartsValueCHF, 'Eingekaufte Ersatzteile', { optional: true });

  // Druckluft / Kompressor
  if (formData.hasCompressedAir) {
    if (!formData.compressorType) {
      errors.push({
        step: 4,
        field: 'compressorType',
        message: 'Kompressor-Typ ist erforderlich, wenn Druckluft / Kompressor im Einsatz ist.',
      });
    }

    if (
      !hasValue(formData.compressorBuildYear)
    ) {
      errors.push({
        step: 4,
        field: 'compressorBuildYear',
        message: 'Baujahr ist erforderlich, wenn Druckluft / Kompressor im Einsatz ist.',
      });
    }

    checkBuildYear(4, 'compressorBuildYear', formData.compressorBuildYear, 'Kompressor');

    if (
      !hasValidNumber(formData.compressedAirPressureBar) ||
      parseNumericInput(formData.compressedAirPressureBar) <= 0
    ) {
      errors.push({
        step: 4,
        field: 'compressedAirPressureBar',
        message: 'Netzdruck (bar) ist erforderlich und muss grösser als 0 sein.',
      });
    }

    if (!formData.compressorHeatRecovery) {
      errors.push({
        step: 4,
        field: 'compressorHeatRecovery',
        message: 'Abwärmenutzung ist erforderlich, wenn Druckluft / Kompressor im Einsatz ist.',
      });
    }

    if (!formData.compressedAirLeaks) {
      errors.push({
        step: 4,
        field: 'compressedAirLeaks',
        message: 'Leckagen im Druckluftnetz sind erforderlich, wenn Druckluft / Kompressor im Einsatz ist.',
      });
    }

    if (!formData.pneumaticGrinders) {
      errors.push({
        step: 4,
        field: 'pneumaticGrinders',
        message: 'Angabe zu pneumatischen Schleifmaschinen ist erforderlich, wenn Druckluft / Kompressor im Einsatz ist.',
      });
    }
  }

  // Hinweise
  if (hasPublicChargingStations) {
    const chargingPoints = parseNumericInput(publicChargingPointsCount);

    if (
      !hasValidNumber(publicChargingPointsCount) ||
      Number.isNaN(chargingPoints) ||
      chargingPoints <= 0
    ) {
      errors.push({
        step: 1,
        field: 'publicChargingPointsCount',
        message:
          'Bitte Anzahl Ladepunkte angeben, wenn öffentliche Ladestationen vorhanden. Wert muss grösser als 0 sein.',
      });
    }
  }

  if (pvStatus === 'vorhanden') {
    if (hasValue(pvProductionKwh)) {
      const pvProduction = parseNumericInput(pvProductionKwh);

      if (!hasValidNumber(pvProductionKwh) || pvProduction <= 0) {
        errors.push({
          step: 2,
          field: 'pvProductionKwh',
          message: 'PV-Produktion muss grösser als 0 sein.',
        });
      }
    } else {
      warnings.push({
        step: 2,
        field: 'pvProductionKwh',
        message: 'PV-Produktion empfohlen, wenn PV vorhanden.',
      });
    }

    if (hasValue(pvFeedInKwh)) {
      const pvFeedIn = parseNumericInput(pvFeedInKwh);
      const pvProduction = hasValue(pvProductionKwh)
        ? parseNumericInput(pvProductionKwh)
        : null;

      if (!hasValidNumber(pvFeedInKwh) || pvFeedIn < 0) {
        errors.push({
          step: 2,
          field: 'pvFeedInKwh',
          message: 'Rückspeisung darf nicht negativ sein.',
        });
      } else if (
        pvProduction !== null &&
        Number.isFinite(pvProduction) &&
        pvFeedIn > pvProduction
      ) {
        errors.push({
          step: 2,
          field: 'pvFeedInKwh',
          message: 'Rückspeisung darf nicht grösser sein als die PV-Produktion.',
        });
      }
    }
  }

  return {
    errors,
    warnings,
    isValid: errors.length === 0,
  };
}
